import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, Sparkles, Wine as WineIcon, Award, X, Check } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useWine } from '@/features/wines/useWines';
import {
  ACIDEZ, ALCOHOL, AROMAS_PRIMARIOS, AROMAS_SECUNDARIOS, AROMAS_TERCIARIOS,
  BURBUJA, CLARIDAD, COLOR_BLANCO, COLOR_ROSADO, COLOR_TINTO, CONDICION_NARIZ,
  CUERPO, DULZOR, ESCALA_5, EVOLUCION, FINAL, INTENSIDAD_ASPECTO,
  NIVEL_CALIDAD, POTENCIAL_GUARDA, TANINO,
} from './tastingOptions';
import type {
  AromaGroup, BocaData, ConclusionData, NarizData, TastingNote, VistaData,
} from './tastingOptions';
import { useSaveTastingNote, useTastingNote } from './useTastingNotes';

type Tab = 'aspecto' | 'nariz' | 'boca' | 'conclusiones';

interface FormState {
  tasted_on: string;
  vista: VistaData;
  nariz: NarizData;
  boca: BocaData;
  conclusion: string;
  conclusion_data: ConclusionData;
  score: number | '';
  scale: '100' | '20' | 'propio';
}

const today = () => new Date().toISOString().slice(0, 10);

const empty: FormState = {
  tasted_on: today(),
  vista: {},
  nariz: { aromas_primarios: [], aromas_secundarios: [], aromas_terciarios: [] },
  boca: {},
  conclusion: '',
  conclusion_data: {},
  score: '',
  scale: '100',
};

export function TastingForm() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const noteId = params.id ? Number(params.id) : undefined;
  const wineIdFromQuery = searchParams.get('wine_id');
  const { data: editing } = useTastingNote(noteId);

  const wineId = noteId ? editing?.wine_id : wineIdFromQuery ? Number(wineIdFromQuery) : undefined;
  const { data: wine } = useWine(wineId);
  const save = useSaveTastingNote();

  const [tab, setTab] = useState<Tab>('aspecto');
  const [form, setForm] = useState<FormState>(empty);

  useEffect(() => {
    if (editing) {
      setForm({
        tasted_on: editing.tasted_on,
        vista: editing.vista ?? {},
        nariz: editing.nariz ?? {
          aromas_primarios: [],
          aromas_secundarios: [],
          aromas_terciarios: [],
        },
        boca: editing.boca ?? {},
        conclusion: editing.conclusion ?? '',
        conclusion_data: editing.conclusion_data ?? {},
        score: editing.score ?? '',
        scale: editing.scale,
      });
    }
  }, [editing]);

  if (!wineId) return <div className="p-4 md:p-8 text-notion-500">Sin vino seleccionado.</div>;
  if (!wine) return <div className="p-4 md:p-8 text-notion-500">Cargando vino…</div>;

  const colorOptions =
    wine.type === 'tinto' ? COLOR_TINTO : wine.type === 'rosado' ? COLOR_ROSADO : COLOR_BLANCO;
  const isRed = wine.type === 'tinto';
  const isSparkling = wine.type === 'espumante';

  const updateVista = (patch: Partial<VistaData>) =>
    setForm((f) => ({ ...f, vista: { ...f.vista, ...patch } }));
  const updateNariz = (patch: Partial<NarizData>) =>
    setForm((f) => ({ ...f, nariz: { ...f.nariz, ...patch } }));
  const updateBoca = (patch: Partial<BocaData>) =>
    setForm((f) => ({ ...f, boca: { ...f.boca, ...patch } }));
  const updateConclusion = (patch: Partial<ConclusionData>) =>
    setForm((f) => ({ ...f, conclusion_data: { ...f.conclusion_data, ...patch } }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<TastingNote> = {
      wine_id: wineId,
      tasted_on: form.tasted_on,
      vista: form.vista,
      nariz: form.nariz,
      boca: form.boca,
      conclusion: form.conclusion || null,
      conclusion_data: form.conclusion_data,
      score: form.score === '' ? null : Number(form.score),
      scale: form.scale,
    };
    const saved = await save.mutateAsync({ id: noteId, payload });
    navigate(`/wines/${saved.wine_id}`);
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <button
        type="button"
        onClick={() => navigate(`/wines/${wineId}`)}
        className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> Volver al vino
      </button>

      <header className="mb-5">
        <h1 className="text-xl md:text-2xl font-medium">
          {noteId ? 'Editar nota de cata' : 'Nueva nota de cata'}
        </h1>
        <p className="text-sm text-notion-500">
          {wine.name} · {wine.vintage ?? '—'} · {wine.format}
        </p>
        <p className="text-[11px] text-notion-400 mt-1 tracking-wide uppercase">
          Técnica Sistemática de Cata WSET® · Nivel 3
        </p>
      </header>

      <div className="flex items-center border-b border-notion-200 mb-4 overflow-x-auto">
        <TabButton active={tab === 'aspecto'} onClick={() => setTab('aspecto')} icon={<Eye size={14} />}>
          Aspecto
        </TabButton>
        <TabButton active={tab === 'nariz'} onClick={() => setTab('nariz')} icon={<Sparkles size={14} />}>
          Nariz
        </TabButton>
        <TabButton active={tab === 'boca'} onClick={() => setTab('boca')} icon={<WineIcon size={14} />}>
          Boca
        </TabButton>
        <TabButton active={tab === 'conclusiones'} onClick={() => setTab('conclusiones')} icon={<Award size={14} />}>
          Conclusiones
        </TabButton>
      </div>

      <form onSubmit={submit} className="card p-4 md:p-6 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Fecha de la cata</label>
            <input
              type="date"
              className="input"
              value={form.tasted_on}
              max={today()}
              onChange={(e) => setForm({ ...form, tasted_on: e.target.value })}
            />
          </div>
        </div>

        {tab === 'aspecto' && (
          <div className="space-y-4">
            <SelectField
              label="Claridad"
              value={form.vista.claridad ?? ''}
              onChange={(v) => updateVista({ claridad: v })}
              options={CLARIDAD}
            />
            <SelectField
              label="Intensidad"
              value={form.vista.intensidad ?? ''}
              onChange={(v) => updateVista({ intensidad: v })}
              options={INTENSIDAD_ASPECTO}
            />
            <SelectField
              label="Color"
              value={form.vista.color ?? ''}
              onChange={(v) => updateVista({ color: v })}
              options={colorOptions}
            />
            <div>
              <label className="label">Otras observaciones</label>
              <textarea
                className="input"
                rows={2}
                value={form.vista.observaciones ?? ''}
                onChange={(e) => updateVista({ observaciones: e.target.value })}
                placeholder="ej. lágrimas/piernas, sedimento, efervescencia, burbujas"
              />
            </div>
          </div>
        )}

        {tab === 'nariz' && (
          <div className="space-y-4">
            <SelectField
              label="Condición"
              value={form.nariz.condicion ?? ''}
              onChange={(v) => updateNariz({ condicion: v })}
              options={CONDICION_NARIZ}
            />
            <SelectField
              label="Intensidad"
              value={form.nariz.intensidad ?? ''}
              onChange={(v) => updateNariz({ intensidad: v })}
              options={ESCALA_5}
            />
            <SelectField
              label="Evolución"
              value={form.nariz.evolucion ?? ''}
              onChange={(v) => updateNariz({ evolucion: v })}
              options={EVOLUCION}
            />

            <AromaSelector
              title="Aromas primarios"
              subtitle="De la uva y de la fermentación alcohólica"
              options={AROMAS_PRIMARIOS}
              selected={form.nariz.aromas_primarios ?? []}
              onChange={(v) => updateNariz({ aromas_primarios: v })}
            />

            <AromaSelector
              title="Aromas secundarios"
              subtitle="De las opciones de vinificación posteriores a la fermentación"
              options={AROMAS_SECUNDARIOS}
              selected={form.nariz.aromas_secundarios ?? []}
              onChange={(v) => updateNariz({ aromas_secundarios: v })}
            />

            <AromaSelector
              title="Aromas terciarios"
              subtitle="Del envejecimiento"
              options={AROMAS_TERCIARIOS}
              selected={form.nariz.aromas_terciarios ?? []}
              onChange={(v) => updateNariz({ aromas_terciarios: v })}
            />

            <div>
              <label className="label">Características del aroma</label>
              <textarea
                className="input"
                rows={3}
                value={form.nariz.caracteristicas ?? ''}
                onChange={(e) => updateNariz({ caracteristicas: e.target.value })}
                placeholder="Descriptores específicos: ciruela negra madura, vainilla, cuero…"
              />
            </div>
          </div>
        )}

        {tab === 'boca' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SelectField label="Dulzor" value={form.boca.dulzor ?? ''} onChange={(v) => updateBoca({ dulzor: v })} options={DULZOR} />
              <SelectField label="Acidez" value={form.boca.acidez ?? ''} onChange={(v) => updateBoca({ acidez: v })} options={ACIDEZ} />
              {isRed && (
                <SelectField label="Tanino" value={form.boca.tanino ?? ''} onChange={(v) => updateBoca({ tanino: v })} options={TANINO} />
              )}
              <SelectField label="Alcohol" value={form.boca.alcohol ?? ''} onChange={(v) => updateBoca({ alcohol: v })} options={ALCOHOL} />
              <SelectField label="Cuerpo" value={form.boca.cuerpo ?? ''} onChange={(v) => updateBoca({ cuerpo: v })} options={CUERPO} />
              {isSparkling && (
                <SelectField label="Burbuja" value={form.boca.burbuja ?? ''} onChange={(v) => updateBoca({ burbuja: v })} options={BURBUJA} />
              )}
              <SelectField label="Intensidad del sabor" value={form.boca.intensidad_sabor ?? ''} onChange={(v) => updateBoca({ intensidad_sabor: v })} options={ESCALA_5} />
              <SelectField label="Final" value={form.boca.final ?? ''} onChange={(v) => updateBoca({ final: v })} options={FINAL} />
            </div>

            <div>
              <label className="label">Características del sabor</label>
              <textarea
                className="input"
                rows={3}
                value={form.boca.caracteristicas ?? ''}
                onChange={(e) => updateBoca({ caracteristicas: e.target.value })}
                placeholder="ej. fruta negra madura, especias, roble bien integrado…"
              />
            </div>
          </div>
        )}

        {tab === 'conclusiones' && (
          <div className="space-y-4">
            <p className="text-xs uppercase tracking-wide text-notion-500">Evaluación de la calidad</p>
            <SelectField
              label="Nivel de calidad"
              value={form.conclusion_data.nivel_calidad ?? ''}
              onChange={(v) => updateConclusion({ nivel_calidad: v })}
              options={NIVEL_CALIDAD}
            />
            <SelectField
              label="Estado para el consumo / potencial para envejecimiento"
              value={form.conclusion_data.potencial_guarda ?? ''}
              onChange={(v) => updateConclusion({ potencial_guarda: v })}
              options={POTENCIAL_GUARDA}
            />

            <div>
              <label className="label">Conclusión / razones</label>
              <textarea
                className="input"
                rows={5}
                value={form.conclusion}
                onChange={(e) => setForm({ ...form, conclusion: e.target.value })}
                placeholder="Razones que justifican el nivel de calidad, recomendación, maridajes…"
              />
            </div>

            <div className="grid grid-cols-3 gap-3 items-end pt-2 border-t border-notion-100">
              <div className="col-span-2">
                <label className="label">Puntaje (opcional)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="input text-xl tabular-nums"
                  value={form.score}
                  onChange={(e) =>
                    setForm({ ...form, score: e.target.value === '' ? '' : Number(e.target.value) })
                  }
                  placeholder="—"
                />
              </div>
              <div>
                <label className="label">Escala</label>
                <select
                  className="input"
                  value={form.scale}
                  onChange={(e) => setForm({ ...form, scale: e.target.value as FormState['scale'] })}
                >
                  <option value="100">/100</option>
                  <option value="20">/20</option>
                  <option value="propio">propia</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 gap-3 border-t border-notion-100">
          <button type="button" className="btn-secondary" onClick={() => navigate(`/wines/${wineId}`)}>
            <X size={14} /> Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            <Check size={14} />
            {save.isPending ? 'Guardando…' : noteId ? 'Actualizar cata' : 'Guardar cata'}
          </button>
        </div>
      </form>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-sm border-b-2 -mb-px transition inline-flex items-center gap-1.5 whitespace-nowrap',
        active
          ? 'border-notion-800 text-notion-800 font-medium'
          : 'border-transparent text-notion-500 hover:text-notion-800',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function AromaSelector({
  title,
  subtitle,
  options,
  selected,
  onChange,
}: {
  title: string;
  subtitle: string;
  options: AromaGroup[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (v: string) => {
    const set = new Set(selected);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange(Array.from(set));
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-notion-500">{title}</p>
      <p className="text-[11px] text-notion-400 mb-2 italic">{subtitle}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((g) => {
          const active = selected.includes(g.value);
          return (
            <button
              type="button"
              key={g.value}
              onClick={() => toggle(g.value)}
              title={g.hint}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                active
                  ? 'bg-notion-800 text-white border-notion-800'
                  : 'bg-white border-notion-300 text-notion-600 hover:border-notion-500',
              )}
            >
              {g.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
